import * as RadioGroup from '@radix-ui/react-radio-group';
import { ANALYTICS_EVENTS, colors, PDP_POINTS_BANNER_ENABLED } from '@nandosaus/constants';
import { get, includes, isEmpty, minBy, size } from 'lodash';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import { priceFormatter } from '@nandosaus/state-management/lib/util/formatter';
import PropTypes from 'prop-types';

import { ModalFloatingHeader } from '@components/modal/new/modal-header';
import { MealChoices } from '@components/meal-choices';
import { HeadingWithFlameHighlights } from '@components/heading-with-flame-highlights';
import { H4, P } from '../typography/new';
import { AllergyInformation } from '../allergy-information';
import { analytics } from '../../analytics';
import { Box, Flex } from '../grid';
import { Button } from '../button';
import { ByoProductContent } from './byo/product-content';
import { ChoicePicker } from './choice-picker';
import { ConfirmButton } from '../confirm-button';
import { Modal } from '../modal/new';
import { NutritionalInformation } from '../nutritional-information';
import { ProductDetailHeader } from './product-detail-header';
import { ProductSizePicker } from './product-size-picker';
import { QuantityPicker } from '../quantity-picker';
import { RemoveIngredientsScreen } from './remove-ingredients-screen';
import { OptionLabel } from './choice-picker/option-card';
import { useFeature } from '../../hooks/use-feature';

const MODAL_SCREENS = {
  DEFAULT: 'default',
  INGREDIENTS: 'ingredients',
  MEAL: 'meal',
  REMOVE_INGREDIENTS: 'remove',
  ALLERGEN_AND_NUTRITIONAL: 'allergen-and-nutrional',
};

const ProductDetailModal = observer(
  ({ modalOpen, handleModalClose, handleModalSubmit: handleModalSubmitProp, showMealCta, saveToCartAttribution }) => {
    const handleModalSubmit = handleModalSubmitProp || handleModalClose;
    const [highlightMissingRequiredChoices, setHighlightMissingRequiredChoices] = useState(false);
    const [showRemoveIngredientMenu, setShowRemoveIngredientMenu] = useState(false);
    const [showAllergenAndNutritionalModal, setShowAllergenAndNutritionalModal] = useState(false);
    const { ProductDetailsState, CartStore, GroupOrderStore, MenuStore, FeatureFlagStore } = useRootStore();
    const sizePickerEnabled = useFeature('product-size-picker');

    const menuItemGroups = FeatureFlagStore.getFeatureFlag('menu-item-groups');

    const {
      buildYourOwnChoiceStep,
      displayProduct,
      isLastStep,
      orderItem,
      product,
      quantity,
      nextStepReady,
    } = ProductDetailsState;

    const inputMax = 100;

    const showPerksSash = useMemo(
      () => PDP_POINTS_BANNER_ENABLED && get(orderItem, 'product.points') > 0 && !GroupOrderStore?.isGroupInvitee,
      [GroupOrderStore?.isGroupInvitee, orderItem]
    );

    const choiceNameRefs = useRef({});
    const modalScrollTop = useCallback(node => {
      if (node !== null) {
        node.scrollIntoView(true);
      }
    }, []);

    const modalScroll = useCallback(
      node => {
        if (node !== null) {
          node.scrollIntoView(ProductDetailsState.id ? { block: 'end' } : true);
        }
      },
      [ProductDetailsState.id]
    );

    const scrollToFirstMissingRequiredChoiceNode = useCallback(() => {
      if (isEmpty(ProductDetailsState.missingRequiredChoices)) {
        return;
      }

      const firstMissingRequiredChoiceNode = choiceNameRefs.current[ProductDetailsState.missingRequiredChoices[0]];
      if (firstMissingRequiredChoiceNode) {
        firstMissingRequiredChoiceNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, [ProductDetailsState.missingRequiredChoices]);

    const isMixedBastingEnabled = useFeature('mixed-basting');
    useEffect(() => {
      if (!isMixedBastingEnabled) {
        // on selection of a choice, scroll user to next required choice
        scrollToFirstMissingRequiredChoiceNode();
      }
    }, [ProductDetailsState.missingRequiredChoices, scrollToFirstMissingRequiredChoiceNode, isMixedBastingEnabled]);

    const multiOptionGroup = useMemo(() => {
      if (!menuItemGroups || !Array.isArray(menuItemGroups) || !product) return null;
      const { plu } = product;
      const restaurantId = MenuStore.preferences?.restaurantId;
      return (
        menuItemGroups.find(
          group =>
            group.enabled &&
            group.options.some(o => o.plu === plu) &&
            (isEmpty(group.restaurantIds) || group.restaurantIds.map(String).includes(String(restaurantId)))
        ) || null
      );
    }, [menuItemGroups, product, MenuStore.preferences?.restaurantId]);

    const multiOptionData = useMemo(() => {
      if (!multiOptionGroup) return null;
      const items = multiOptionGroup.options
        .map(option => ({ option, product: MenuStore.menu?.getProductByPlu(option.plu) }))
        .filter(({ product: optionProduct }) => optionProduct?.isAvailable);
      // Hide the picker when there's nothing to choose between — a single forced-selected
      // radio is confusing UX; the PDP already opens with that product pre-selected.
      if (items.length <= 1) return null;
      const cheapest = minBy(items, item => item.product.prices.cents);
      const baseCents = cheapest?.product?.prices?.cents || 0;
      return { items, baseCents };
    }, [multiOptionGroup, MenuStore.menu]);

    const switchMultiOption = useCallback(
      optionProduct => {
        ProductDetailsState.switchMultiOptionProduct(optionProduct, {
          retainChoices: multiOptionGroup?.retainChoices,
        });
      },
      [ProductDetailsState, multiOptionGroup]
    );

    const isByoProduct = get(product, 'isByoProduct');
    const byoChoices = isByoProduct ? get(product, 'byoChoices', []) : [];

    // Get catering-specific quantity settings from the product
    const cateringMinQty = product?.minQty || 1;
    const cateringQtyIncrement = product?.qtyIncrement || 1;

    const handleQuantityChange = useCallback(newQuantity => ProductDetailsState.updateQuantity(newQuantity), [
      ProductDetailsState,
    ]);

    // Set initial quantity to minQty when in catering mode
    useEffect(() => {
      if (MenuStore.isCateringMenu && cateringMinQty > 1 && quantity < cateringMinQty && !ProductDetailsState.id) {
        // Only set to minQty if we're not editing an existing cart item and current quantity is below minQty
        handleQuantityChange(cateringMinQty);
      }
    }, [MenuStore.isCateringMenu, cateringMinQty, quantity, ProductDetailsState.id, handleQuantityChange]);

    // Enhanced increment function for catering mode
    const handleIncrement = useCallback(() => {
      handleQuantityChange(quantity + 1);
    }, [quantity, handleQuantityChange]);

    // Enhanced decrement function for catering mode
    const handleDecrement = useCallback(() => {
      handleQuantityChange(quantity - 1);
    }, [quantity, handleQuantityChange]);

    // Reset ProductDetailsState on unmount
    useEffect(() => {
      return () => {
        ProductDetailsState.reset();
      };
    }, [ProductDetailsState]);

    if (!product) {
      return null;
    }

    const hasSelectedMealChoiceOptions = size(ProductDetailsState.chosenMealChoiceOptions) > 0;
    const modalButtonTitle = hasSelectedMealChoiceOptions ? `${displayProduct.name} Meal` : displayProduct.name;

    const buttonText = (() => {
      if (ProductDetailsState.isEditingCartItem) {
        return 'Update order';
      }

      if (ProductDetailsState.orderItem.isMealProduct) {
        return 'Add meal to order';
      }

      if (ProductDetailsState.displayProduct.hasMealUpgrade) {
        return 'Add to order';
      }

      return ProductDetailsState.quantity > 1 ? 'Add items' : 'Add item';
    })();

    const confirmButton = (
      <ConfirmButton
        price={ProductDetailsState.totals.formattedPrice}
        kilojoules={ProductDetailsState.formattedKilojoules}
        disabled={ProductDetailsState.loading}
        buttonText={buttonText}
        onClick={() => {
          if (ProductDetailsState.isEditingCartItem) {
            const { plu, name, productCategory } = orderItem.product;
            const { isReorderItem } = orderItem;

            analytics.track(ANALYTICS_EVENTS.ORDER_UPDATED, {
              cartId: CartStore.cartId,
              coupon: CartStore.offer ? CartStore.offer.description : undefined,
              value: orderItem.totals.dollarValue,
              product_id: plu,
              name,
              quantity: orderItem.quantity,
              category: productCategory?.name,
              isRecommendedItem: orderItem.isRecommendedItem,
            });

            if (isReorderItem) {
              analytics.track(ANALYTICS_EVENTS.REORDER_ITEM_EDITED, {
                cartId: CartStore.cartId,
                product_id: plu,
                name,
                quantity: orderItem.quantity,
                category: productCategory?.name,
                isRecommendedItem: orderItem.isRecommendedItem,
              });
            }
          }

          if (ProductDetailsState.isValid) {
            // Attribution has to be threaded through here: a product configured in the PDP
            // after being picked from the upsell panel would otherwise lose its isUpsellItem
            // flag, which is only set on the direct-add path.
            ProductDetailsState.saveToCart(saveToCartAttribution);
            handleModalSubmit();
          } else {
            setHighlightMissingRequiredChoices(true);
            scrollToFirstMissingRequiredChoiceNode();
          }
        }}
        variant="action"
        isFullWidth
      />
    );

    const handleContinue = () => {
      if (!isLastStep) {
        ProductDetailsState.nextStep();
      }
    };

    const handleBack = () => {
      if (!buildYourOwnChoiceStep) {
        handleModalClose();
        ProductDetailsState.resetStep();
      } else {
        ProductDetailsState.previousStep();
      }
    };

    const modalFooter = (
      <>
        {isByoProduct && !isLastStep ? (
          <Flex justifyContent="flex-start" width="100%" gap="12px">
            <Button variant="secondary" backgroundColor="grey50" isFullWidth onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="secondary"
              isFullWidth
              backgroundColor="grey50"
              onClick={handleContinue}
              disabled={!nextStepReady()}
            >
              Continue
            </Button>
          </Flex>
        ) : (
          <>
            <Box flexGrow={1}>{confirmButton}</Box>
          </>
        )}
      </>
    );

    const modalProps = {
      modalOpen,
      modalFooter,
      modalButtonTitle,
      modalScroll,
      modalScreens: MODAL_SCREENS,
      handleModalClose,
    };

    const productDetailProps = {
      sizePickerEnabled,
      byoChoices,
      showPerksSash,
      showMealCta,
      handleQuantityChange,
      highlightMissingRequiredChoices,
    };

    if (isByoProduct) {
      return <ByoProductContent {...modalProps} {...productDetailProps} />;
    }

    return (
      <Modal
        handleClose={handleModalClose}
        modalTitle={displayProduct.name}
        open={modalOpen}
        buttonTitle={modalButtonTitle}
        buttonSecondaryTitle={ProductDetailsState.formattedKilojoules}
        buttonPrice={ProductDetailsState.totals.formattedPrice}
        footer={modalFooter}
        footerPosition="relative"
        isDividerHidden
        showShadow
        header={<ModalFloatingHeader handleClose={handleModalClose} />}
        hideBottomDecal
        variant="side"
        bg="transparent"
        mx="0"
      >
        <Flex flexDirection="column" bg="black">
          <Box ref={modalScroll} height="fit-content">
            <ProductDetailHeader
              product={displayProduct}
              points={ProductDetailsState.formattedPoints}
              showPerksSash={showPerksSash}
            />
          </Box>
          <Box bg={colors.warmWhite100} borderTopLeftRadius="32px" borderTopRightRadius="32px">
            {sizePickerEnabled && product.hasOtherSizes && (
              <Box pt="32px">
                <ProductSizePicker
                  sizes={product.sizes}
                  onChange={newSize => {
                    ProductDetailsState.setSize(newSize);
                  }}
                  value={product.id}
                />
              </Box>
            )}
            {product.initialChoices.map((choice, index) => {
              const isBasting = choice.type === 'basting';
              const nextChoice = product.initialChoices[index + 1];
              const isLastBasting = isBasting && (!nextChoice || nextChoice.type !== 'basting');
              const showMealPicker = multiOptionData && isLastBasting;

              const choiceElement = (
                <Box
                  key={choice.name}
                  id={choice.name}
                  ref={node => {
                    choiceNameRefs.current[choice.name] = node;
                  }}
                  borderTop={index === 0 ? 'none' : '4px solid'}
                  borderColor="warmWhite3"
                  pb="32px"
                  pt="32px"
                  minWidth="100%"
                >
                  <ChoicePicker
                    choice={choice}
                    isChoiceInvalid={
                      highlightMissingRequiredChoices &&
                      includes(ProductDetailsState.missingRequiredChoices, choice.name)
                    }
                    index={index}
                  />
                </Box>
              );

              if (!showMealPicker) return choiceElement;

              const selectedSubtitle = multiOptionData.items.find(({ option }) => option.plu === product?.plu)?.option
                ?.subtitle;

              return (
                <Fragment key={choice.name}>
                  {choiceElement}
                  <Box borderTop="4px solid" borderColor="warmWhite3" pb="32px" pt="32px" minWidth="100%">
                    <Flex px={{ xs: '16px', md: '32px' }} flexDirection="column">
                      <H4 my="0">Choose your meal</H4>
                      {!!selectedSubtitle && (
                        <P variant="small" mt="8px">
                          {selectedSubtitle}
                        </P>
                      )}
                    </Flex>
                    <Box px={{ xs: '16px', md: '32px' }} pt="16px">
                      <RadioGroup.Root
                        value={product.plu}
                        orientation="vertical"
                        style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                      >
                        {multiOptionData.items.map(({ option, product: optionProduct }) => {
                          const optionCents = optionProduct?.prices?.cents || 0;
                          const diffCents = optionCents - multiOptionData.baseCents;
                          const priceText = diffCents > 0 ? `+ ${priceFormatter(diffCents)}` : undefined;

                          const optionTags = option.bestValue === true ? [{ label: 'Best Value' }] : undefined;

                          return (
                            <RadioGroup.Item
                              key={option.plu}
                              value={option.plu}
                              asChild
                              onClick={() => {
                                if (optionProduct && option.plu !== product.plu) {
                                  switchMultiOption(optionProduct);
                                }
                              }}
                            >
                              <OptionLabel
                                option={{ label: option.label, text: priceText, tags: optionTags }}
                                showOptionImage={false}
                              />
                            </RadioGroup.Item>
                          );
                        })}
                      </RadioGroup.Root>
                    </Box>
                  </Box>
                </Fragment>
              );
            })}
            {product.extraChoice ? (
              <Box
                key={product.extraChoice.name}
                id={product.extraChoice.name}
                ref={node => {
                  choiceNameRefs.current[product.extraChoice.name] = node;
                }}
                pb="32px"
              >
                <ChoicePicker
                  choice={product.extraChoice}
                  isChoiceInvalid={
                    highlightMissingRequiredChoices &&
                    includes(ProductDetailsState.missingRequiredChoices, product.extraChoice.name)
                  }
                />
              </Box>
            ) : null}
            {product.removeChoice && !showRemoveIngredientMenu ? (
              <Box
                px={{ xs: '16px', md: '32px' }}
                pt={!product.initialChoices.length && !product.hasOtherSizes && '32px'}
                pb="32px"
              >
                <Button
                  variant="secondary"
                  isFullWidth
                  onClick={() => setShowRemoveIngredientMenu(!showRemoveIngredientMenu)}
                  backgroundColor="transparent"
                >
                  Remove ingredients
                </Button>
              </Box>
            ) : null}
            {showRemoveIngredientMenu && (
              <Box px={{ xs: '16px', md: '32px' }} pb="32px">
                <RemoveIngredientsScreen />
              </Box>
            )}
            {showAllergenAndNutritionalModal ? (
              <>
                <Box
                  bg="rgba(0, 0, 0, 0.3)"
                  position="absolute"
                  height="100%"
                  width="100%"
                  zIndex={2}
                  top={0}
                  onClick={() => {
                    setShowAllergenAndNutritionalModal(false);
                  }}
                />

                <Box
                  bg={colors.warmWhite100}
                  position="absolute"
                  bottom={0}
                  width="100%"
                  maxHeight="66%"
                  zIndex={100}
                  overflowY="auto"
                  borderTopLeftRadius="32px"
                  borderTopRightRadius="32px"
                >
                  <Flex px="16px" py="32px" ref={modalScrollTop} gap="16px" flexDirection="column">
                    <AllergyInformation />
                    <NutritionalInformation />
                  </Flex>
                </Box>
              </>
            ) : null}
            {displayProduct.hasMealUpgrade && showMealCta && (
              <Box borderTop="4px solid" borderColor="warmWhite3" py="32px">
                <Flex px={{ xs: '16px', md: '32px' }}>
                  <H4 my="0">Make it a meal</H4>
                </Flex>
                <P px={{ xs: '16px', md: '32px' }} mt="8px" mb={{ xs: '28px', md: '36px' }} variant="standard">
                  Add a regular side and a drink from {product.mealUpgradePrices().formattedPrice}
                </P>
                <MealChoices />
              </Box>
            )}
            <Flex
              px={{ xs: '16px', md: '32px' }}
              py="24px"
              justifyContent="space-between"
              alignItems="center"
              borderTop={product?.choices?.length && '4px solid'}
              borderBottom="4px solid"
              borderColor="warmWhite3"
            >
              <HeadingWithFlameHighlights mb="0" heading="Quantity" variant="section" />
              <QuantityPicker
                quantity={quantity}
                increment={handleIncrement}
                decrement={handleDecrement}
                onQuantityChange={value => handleQuantityChange(value)}
                max={inputMax}
                min={1}
                isCateringMode={MenuStore.isCateringMenu}
                minQty={cateringMinQty}
                qtyIncrement={cateringQtyIncrement}
                disableInput={MenuStore.isCateringMenu && (cateringMinQty > 1 || cateringQtyIncrement > 1)}
                showMinusOnMin
              />
            </Flex>
            {orderItem.showAllergenInfo || orderItem.showNutritionalInfo ? (
              <Flex justifyContent="center" pt="32px">
                <P
                  variant="text-link"
                  textAlign="center"
                  onClick={() => setShowAllergenAndNutritionalModal(true)}
                  style={{ color: colors.black, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Allergen and Nutritional Information
                </P>
              </Flex>
            ) : null}
            {ProductDetailsState.id && (
              <Flex pt={2} pb={2} justifyContent="center">
                {/* @NOTE: removeFromCart() removes the order item from both
                  the ProductDetailsState and the CartStore. */}
                <P
                  variant="text-link"
                  textAlign="center"
                  textDecoration="underline"
                  as="button"
                  onClick={() => {
                    ProductDetailsState.removeFromCart();
                    handleModalClose();
                  }}
                >
                  Remove from order
                </P>
              </Flex>
            )}
          </Box>
        </Flex>
      </Modal>
    );
  }
);

ProductDetailModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  handleModalSubmit: PropTypes.func,
  showMealCta: PropTypes.bool,
  // Origin flags CartStore.addOrderItem accepts (isUpsellItem / isRecommendedItem /
  // isReorderItem). Ignored when editing an existing cart line, which keeps its own flags.
  saveToCartAttribution: PropTypes.shape({}),
};

ProductDetailModal.defaultProps = {
  handleModalSubmit: null,
  showMealCta: true,
  saveToCartAttribution: undefined,
};

export { ProductDetailModal };
